<template>
  <div>
    <b-card>
      <b-card-title><h3>Tasks by Phase</h3></b-card-title>

      <b-card-text>
        <div>
          <b-row class="mb-2">
            <b-col md="6" lg="6" xs="12">
              <v-select
                v-model="filterProjectId"
                :options="filterProjectIdOption"
                :reduce="(task) => task.id"
                label="name"
                placeholder="Select Project"
                class="mr-sm-1 mb-1 mb-sm-0 custom-font"
                @input="filterProject"
              />
            </b-col>
            <b-col md="6" lg="6" xs="12">
              <v-select
                v-model="filterBoardListMilestoneId"
                :options="filterMilestoneBoardIdOption"
                :reduce="(item) => item.id"
                label="name"
                placeholder="Select Milestone Board"
                class="custom-font"
                @input="getPhaseData"
              />
            </b-col>
          </b-row>
        </div>

        <div
          class="chart-container d-flex justify-content-center align-items-center"
        >
          <div v-if="isLoading" class="d-flex justify-content-center my-3">
            <b-spinner variant="primary" label="Loading..." />
          </div>
          <div v-else-if="phaseData.length === 0" class="text-center my-5">
            <p v-if="!filterBoardListMilestoneId">Select a board.</p>
            <p v-else>No task data available to display.</p>
          </div>
          <pie-chart
            v-else
            :chart-data="chartData"
            :options="chartOptions"
            :height="300"
            :selected-indexes="selectedAssigneeIndexes"
            @chart-click="handleChartClick"
          />
        </div>
      </b-card-text>
    </b-card>

    <!-- Modal for detailed view -->
    <b-modal
      id="phase-modal"
      centered
      :title="
        selectedAssignee ? `${selectedAssignee}` : 'Task Distribution by Phase'
      "
      hide-footer
      size="xl"
      no-close-on-backdrop
      @hidden="closeModal"
    >
      <b-row>
        <!-- Left: Chart -->
        <b-col cols="12" md="6" class="mb-3">
          <div
            class="modal-chart d-flex justify-content-center align-items-center"
          >
            <pie-chart
              ref="modalPie"
              :chart-data="getModalChartData"
              :options="modalChartOptions"
              :height="400"
              :selected-indexes="selectedAssigneeIndexes"
              @chart-click="handleChartClick"
            />
          </div>
        </b-col>

        <!-- Right: Task List -->
        <b-col cols="12" md="6">
          <div class="modal-task-list">
            <div v-if="isLoading" class="d-flex justify-content-center my-3">
              <b-spinner variant="primary" label="Loading..." />
            </div>
            <div v-else>
              <DataTable
                v-if="selectedPhaseIds.length > 0"
                :key="selectedPhaseIds.join(',')"
                :is-subtask="isSubtask"
                :show="10"
                :endpoint="taskEndpoint"
              />
            </div>
          </div>
        </b-col>
      </b-row>
    </b-modal>
  </div>
</template>

<script>
// Import our custom PieChart component

// For Chart.js v2, we need to import and register controllers
import Chart from "chart.js";

// Import Bootstrap Vue components
import {
  BModal,
  BRow,
  BCol,
  BCard,
  BSpinner,
  BCardText,
  BCardTitle,
} from "bootstrap-vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import DataTable from "./DataTable.vue";
import PieChart from "./PieChart.vue";
import { mapGetters } from "vuex";
// Register the controllers needed for the pie chart
Chart.defaults.global.defaultFontFamily =
  "'Montserrat', Helvetica, Arial, sans-serif";
Chart.defaults.global.defaultFontSize = 12;

export default {
  name: "TaskByPhaseChart",
  components: {
    BCardText,
    "pie-chart": PieChart,
    DataTable,
    BModal,
    BRow,
    BCol,
    BCard,
    BCardTitle,
    BSpinner,
  },
  props: {
    isSubtask: {
      type: Boolean,
      default: false,
    },
    viewAs: {
      type: String,
      default: "employee",
    },
  },
  data() {
    return {
      phaseData: [],
      users: [],
      isLoading: false,
      // Modal state
      showModal: false,
      selectedAssigneeIndexes: [],

      filterProjectIdOption: [],
      filterProjectId: "",

      filterBoardListMilestoneId: "",
      filterMilestoneBoardIdOption: [],
    };
  },
  computed: {
    ...mapGetters({
      authUser: "userModule/getUser",
    }),
    chartData() {
      // Check if phaseData is empty
      if (this.phaseData.length === 0) {
        console.log("chartData: phaseData is empty");
        // Return a minimal valid chart data structure
        return {
          labels: ["No Data"],
          datasets: [
            {
              data: [1],
              backgroundColor: ["#cccccc"],
            },
          ],
        };
      }

      const labels = this.phaseData.map((item) => item.title);
      const colors = this.getColors(this.phaseData.length);

      // Calculate total tasks for each assignee
      let data;

      if (this.isSubtask) {
        data = this.phaseData.map((item) => item.sub_task_count || 0);
      } else {
        data = this.phaseData.map((item) => item.tasks_count || 0);
      }

      const hoverColors = colors.map((color) => this.darkenColor(color, 15)); // 15% darker

      return {
        labels,
        datasets: [
          {
            data,
            backgroundColor: colors.map((color, idx) =>
              this.selectedAssigneeIndexes.length > 0 &&
              !this.selectedAssigneeIndexes.includes(idx)
                ? color.replace("0.8", "0.6")
                : color
            ),
            hoverBackgroundColor: hoverColors,
            borderColor: colors.map((color, idx) =>
              this.selectedAssigneeIndexes.includes(idx)
                ? "#2c3e50"
                : color.replace("0.8", "1")
            ),
            borderWidth: colors.map((_, idx) =>
              this.selectedAssigneeIndexes.includes(idx) ? 3 : 1
            ),
          },
        ],
      };
    },

    chartOptions() {
      // Simplified options for better compatibility
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false,
          position: "bottom",
        },
        tooltips: {
          enabled: true,
        },
        animation: {
          animateScale: true,
          animateRotate: true,
        },
      };
    },
    modalChartOptions() {
      // Simplified options for better compatibility
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: true,
          position: "bottom",

          /* custom click behaviour */
          onClick(e, legendItem) {
            const chart = this.chart || this; // chart instance
            const meta = chart.getDatasetMeta(0); // first (and only) dataset
            const idx = legendItem.index; // data index that was clicked

            // add a flag on the chart the first time we run
            if (typeof chart.$firstLegendClick === "undefined") {
              chart.$firstLegendClick = true;
            }

            if (chart.$firstLegendClick) {
              /* ---------- FIRST CLICK ------------ */
              // show only the clicked slice/bar/point
              meta.data.forEach((el, i) => {
                el.hidden = i !== idx; // hide everything else
              });
              chart.$firstLegendClick = false; // switch to normal mode
            } else {
              /* ---------- SUBSEQUENT CLICKS ------- */
              // toggle just this one item
              const el = meta.data[idx];
              el.hidden = !el.hidden;
            }

            chart.update();
          },
        },
        tooltips: {
          enabled: true,
        },
        title: {
          display: true,
          text: "Task by Phases",
        },
        animation: {
          animateScale: true,
          animateRotate: true,
        },
      };
    },
    getModalChartData() {
      // Check if phaseData is empty
      if (this.phaseData.length === 0) {
        // Return a minimal valid chart data structure
        return {
          labels: ["No Data"],
          datasets: [
            {
              data: [1],
              backgroundColor: ["#cccccc"],
            },
          ],
        };
      }

      const labels = this.phaseData.map((item) => item.title);
      const colors = this.getColors(this.phaseData.length);

      // Calculate total tasks for each assignee
      let data;

      if (this.isSubtask) {
        data = this.phaseData.map((item) => item.sub_task_count || 0);
      } else {
        data = this.phaseData.map((item) => item.tasks_count || 0);
      }

      // Highlight the selected section
      const backgroundColor = colors.map((color, index) => {
        return this.selectedAssigneeIndexes.length > 0 &&
          !this.selectedAssigneeIndexes.includes(index)
          ? color.replace("0.8", "0.4")
          : color;
      });

      const hoverColors = colors.map((color) => this.darkenColor(color, 15)); // 15% darker

      return {
        labels,
        datasets: [
          {
            data,
            backgroundColor,
            hoverBackgroundColor: hoverColors,
            borderColor: colors.map((color, idx) =>
              this.selectedAssigneeIndexes.includes(idx)
                ? "#2c3e50"
                : color.replace("0.8", "1")
            ),
            borderWidth: colors.map((_, idx) =>
              this.selectedAssigneeIndexes.includes(idx) ? 4 : 1
            ),
          },
        ],
      };
    },

    // Computed properties for the modal
    selectedAssignee() {
      if (this.selectedAssigneeIndexes.length === 0) return "";
      return this.selectedAssigneeIndexes
        .map((idx) => this.phaseData[idx]?.title)
        .filter(Boolean)
        .join(", ");
    },
    selectedPhaseIds() {
      if (this.selectedAssigneeIndexes.length === 0) return [];
      return this.selectedAssigneeIndexes
        .map((idx) => this.phaseData[idx]?.id)
        .filter(Boolean);
    },
    taskEndpoint() {
      if (this.selectedPhaseIds.length === 0) return "";

      const base = `api/milestone-task-boards/tasks/all`;

      const params = new URLSearchParams({
        view: this.viewAs,
        include:
          "phase,milestoneTaskBoard.department,milestoneTaskBoard.supervisor,team,task,users",
        // Note: 'users' might be needed to show assignee names in the table
      });

      // We need to add filter for Phase IDs
      this.selectedPhaseIds.forEach((id) => {
        params.append("filterPhaseId[]", id);
      });

      // Also need to preserve the project/board filters context if they were applied to the chart?
      // The chart data comes from `selectedBoard`. So we are filtering within that board.
      if (this.filterBoardListMilestoneId) {
        params.append(
          "filterMilestoneBoardId",
          this.filterBoardListMilestoneId
        );
      }

      return `${base}?${params.toString()}`;
    },
  },
  watch: {
    isSubtask: {
      immediate: true,
      handler(newVal) {
        this.renderPhase();
      },
    },
  },
  async created() {
    try {
      await this.getPhaseData();
      // call all milestone api, if department is selected then don't call milestone board api

      const boards = await this.fetchMilestoneBoards();

      this.filterMilestoneBoardIdOption = boards.map((item) => ({
        name: item.title,
        id: item.id,
        phases: item.phases,
      }));

      // get project approved options
      await this.getApprovedProjects();
    } catch (error) {
      console.log(error);

      this.$toast({
        component: ToastificationContent,
        props: {
          title: "Error",
          icon: "BellIcon",
          variant: "danger",
          text: error?.response?.data?.message || "Failed to load users",
        },
      });
    }
  },

  methods: {
    async filterProject() {
      this.filterBoardListMilestoneId = "";

      try {
        const boards = await this.fetchMilestoneBoards();

        this.filterMilestoneBoardIdOption = boards
          .filter((item) => {
            if (!this.filterProjectId) return true;
            return item.milestone?.data?.project_id === this.filterProjectId;
          })
          .map((item) => ({
            name: item.title,
            id: item.id,
          }));
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error",
            icon: "BellIcon",
            variant: "danger",
            text: error?.response?.data?.message || "Failed to fetch boards",
          },
        });
      }
    },

    async fetchMilestoneBoards() {
      const params = {};

      if (this.viewAs === "employee") {
        params.userId = this.authUser?.id;
      } else if (this.viewAs === "supervisor") {
        params.supervisorId = this.authUser?.id;
      }

      const includeParam = "?include=milestone,phases";
      const response = await this.$api.get(
        `api/milestone-task-boards/all/boards${includeParam}`,
        { params }
      );
      return response?.data?.data || [];
    },

    async getApprovedProjects() {
      let params = {};

      if (this.viewAs === "employee") {
        params.userId = this.authUser?.id;
      } else if (this.viewAs === "supervisor") {
        params.supervisorId = this.authUser?.id;
      }

      const [allApprovedProjects] = await Promise.all([
        this.$api.get("api/projects/approve", { params }),
      ]);

      this.filterProjectIdOption = (allApprovedProjects?.data?.data || []).map(
        (item) => {
          const name = item.lead.title;
          return {
            name,
            id: item.id,
          };
        }
      );
    },

    darkenColor(color, percent = 15) {
      // --- rgba(...) ---------------------------------------------------------
      if (color.startsWith("rgb")) {
        const match = color.match(
          /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/
        );
        if (!match) return color; // safety net

        let [, r, g, b, a] = match.map(Number);
        const delta = Math.round(255 * (percent / 100));

        r = Math.max(0, r - delta);
        g = Math.max(0, g - delta);
        b = Math.max(0, b - delta);

        // keep original alpha if supplied, else default to 1
        return `rgba(${r}, ${g}, ${b}, ${isNaN(a) ? 1 : a})`;
      }

      // --- #rrggbb -----------------------------------------------------------
      const num = parseInt(color.replace("#", ""), 16);
      if (isNaN(num)) return color; // invalid -> give back original

      let r = (num >> 16) - Math.round(255 * (percent / 100));
      let g = ((num >> 8) & 0x00ff) - Math.round(255 * (percent / 100));
      let b = (num & 0x0000ff) - Math.round(255 * (percent / 100));

      r = Math.max(0, r);
      g = Math.max(0, g);
      b = Math.max(0, b);

      return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
    },

    getPhaseData() {
      this.phaseData = [];

      if (!this.filterBoardListMilestoneId) return;

      const selectedBoard = this.filterMilestoneBoardIdOption.find(
        (board) => board.id === this.filterBoardListMilestoneId
      );

      this.phaseData = selectedBoard?.phases?.data || [];
    },

    async renderPhase() {
      const boards = await this.fetchMilestoneBoards();

      this.filterMilestoneBoardIdOption = boards.map((item) => ({
        name: item.title,
        id: item.id,
        phases: item.phases,
      }));

      this.getPhaseData();
    },

    getColors(count) {
      if (count === 0) return [];
      const step = 360 / count;
      return Array.from(
        { length: count },
        (_, i) => `hsla(${i * step}, 70%, 60%, 0.8)`
      );
    },

    // Handle chart click event
    handleChartClick(eventData) {
      if (typeof eventData === "object" && eventData.source === "slice") {
        // Single select for slice clicks
        this.selectedAssigneeIndexes = [eventData.clickedIndex];
      } else if (
        typeof eventData === "object" &&
        eventData.source === "legend"
      ) {
        // Multi select for legend clicks
        this.selectedAssigneeIndexes = eventData.selectedSlices.map(
          (s) => s.index
        );
      } else if (Array.isArray(eventData)) {
        // Fallback
        this.selectedAssigneeIndexes = eventData.map((s) => s.index);
      }
      this.$bvModal.show("phase-modal");
    },

    // We can remove handleModalChartClick as we reuse handleChartClick now
    handleModalChartClick(eventData) {
      this.handleChartClick(eventData);
      // But we don't need to show modal again if it's already open, but it doesn't hurt.
      // Actually `handleChartClick` calls show modal.
    },

    // Close the modal
    closeModal() {
      this.showModal = false;
      this.selectedAssigneeIndexes = [];
    },
  },
};
</script>

<style scoped>
.task-by-assignee {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin: 20px 0;
  position: relative;
}

.chart-title {
  margin-top: 0;
  margin-bottom: 20px;
  color: #2c3e50;
  font-size: 1.2rem;
  font-weight: 600;
  text-align: center;
}

.chart-container {
  position: relative;
  width: 100%;
  min-height: 300px;
  cursor: pointer;
}

/* Modal styles */
.modal-chart {
  width: 100%;
  height: 100%;
  min-height: 400px;
}

.modal-task-list {
  flex: 1;
  border-left: 1px solid #e0e0e0;
  padding-left: 24px;
}

.modal-task-list h4 {
  margin-top: 0;
  margin-bottom: 16px;
  color: #2c3e50;
  font-size: 1.2rem;
}

.task-list {
  max-height: 400px;
  overflow-y: auto;
}

.task-item {
  display: flex;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  background-color: #f9f9f9;
  margin-bottom: 8px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.task-item:hover {
  background-color: #f0f0f0;
}

.task-item.selected-task {
  background-color: #e6f7ff;
  border-left: 3px solid #1890ff;
}

.task-status {
  font-weight: 500;
  color: #333;
}

.task-count {
  color: #666;
}

.no-tasks {
  padding: 16px;
  text-align: center;
  color: #666;
  font-style: italic;
}

@media (max-width: 768px) {
  .chart-container {
    min-height: 250px;
  }

  /* Bootstrap's b-row will automatically stack columns on small screens */
  .modal-task-list {
    border-left: none;
    border-top: 1px solid #e0e0e0;
    padding-top: 24px;
    margin-top: 24px;
  }
}
</style>
